import React from 'react';
import {
    IconHeart,
    IconMessage,
    IconUserPlus,
    IconMessageCircle2,
    IconThumbUp,
    IconUsers,
    IconFolder,
    IconShare,
    IconBell,
    IconDotsVertical
} from '@tabler/icons-react';
import Button from '../../../../../components/ui/Button';
import { timeAgo as sharedTimeAgo } from '../../../../../utils/dateFormat';
import FollowBackButton from '../../../../../components/FollowBackButton';

export const NotificationItem = ({
    notification,
    userCache,
    selectionMode,
    selectedIds,
    processingIds,
    openMenuId,
    currentUser,
    navigate,
    toggleSelected,
    handleNotificationClick,
    handleMarkRead,
    handleDeleteNotification,
    handleFollowRequestAccept,
    handleFollowRequestReject,
    handleAccept,
    handleDecline,
    toggleMenu
}) => {
    const timeAgo = (d) => sharedTimeAgo(d);

    const getNotificationIcon = (type) => {
        const iconProps = { size: 20, className: 'notification-icon' };
        switch (type) {
            case 'follow':
            case 'follow_request':
            case 'follow_request_accepted':
                return <IconUserPlus {...iconProps} className="notification-icon follow" />;
            case 'like':
                return <IconHeart {...iconProps} className="notification-icon like" />;
            case 'comment':
                return <IconMessageCircle2 {...iconProps} className="notification-icon comment" />;
            case 'reply':
                return <IconThumbUp {...iconProps} className="notification-icon reply" />;
            case 'share':
                return <IconShare {...iconProps} className="notification-icon share" />;
            case 'message':
                return <IconMessage {...iconProps} className="notification-icon message" />;
            case 'project_invite':
            case 'project_invite_accepted':
            case 'project_launched':
                return <IconUsers {...iconProps} className="notification-icon project" />;
            case 'solution_vote':
            case 'solution_reason':
                return <IconFolder {...iconProps} className="notification-icon solution" />;
            default:
                return <IconBell {...iconProps} className="notification-icon default" />;
        }
    };

    const getActorAvatar = (notification) => {
        const actorId = notification.actorId || (notification.actorIds && notification.actorIds[0]);
        const url = actorId ? (userCache[actorId]?.photoURL || userCache[actorId]?.userPhotoURL) : null;
        return url || '';
    };

    const getNotificationTitle = (notification) => {
        if (notification.title) return notification.title;
        const actorId = notification.actorId || (notification.actorIds && notification.actorIds[0]);
        const actor = actorId ? userCache[actorId] : null;
        const actorName = actor?.displayName || actor?.username || actor?.name || 'Someone';

        switch (notification.type) {
            case 'message': return `${actorName} sent you a message`;
            case 'mention': return `${actorName} mentioned you`;
            case 'like': return `${actorName} liked your ${notification.targetType || 'post'}`;
            case 'comment': return `${actorName} commented on your post`;
            case 'reply': return `${actorName} replied to your comment`;
            case 'follow': return `${actorName} started following you`;
            case 'follow_request': return `${actorName} requested to follow you`;
            case 'follow_request_accepted': return `${actorName} accepted your follow request`;
            case 'share': return `${actorName} shared your post`;
            case 'project_invite': return `${actorName} invited you to a project`;
            case 'project_invite_accepted': return `${actorName} accepted your project invitation`;
            case 'project_launched': return `Project "${notification.projectTitle || 'your project'}" has launched`;
            case 'solution_vote': return `${actorName} voted on your solution`;
            default: return 'New notification';
        }
    };

    const getNotificationPreview = (notification) => {
        if (notification.previewText) return notification.previewText;
        if (notification.type === 'message' && notification.messageText) return `"${notification.messageText}"`;
        if (notification.type === 'mention' && notification.messageText) return `"${notification.messageText}"`;
        return null;
    };

    return (
        <div
            className={`notification-item ${!notification.read ? 'unread' : ''} ${notification.isAggregated ? 'aggregated' : ''}`}
            onClick={() => {
                if (selectionMode) {
                    if (!notification._isInvite) toggleSelected(notification.id);
                } else if (!notification._isInvite) {
                    handleNotificationClick(notification);
                }
            }}
            onKeyDown={(e) => {
                if (selectionMode && (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter')) {
                    e.preventDefault();
                    if (!notification._isInvite) toggleSelected(notification.id);
                }
            }}
            tabIndex={0}
            aria-selected={selectionMode ? selectedIds.has(notification.id) : undefined}
            style={{ cursor: notification._isInvite ? 'default' : 'pointer' }}
        >
            {getNotificationIcon(notification.type)}

            <div className="notification-content">
                <div className="notification-header">
                    <div className="notification-header-left">
                        {getActorAvatar(notification) && (
                            <img
                                src={getActorAvatar(notification)}
                                alt=""
                                className="notification-avatar"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (notification.actorId) {
                                        if (currentUser?.uid && currentUser.uid === notification.actorId) {
                                            navigate('/profile');
                                        } else {
                                            navigate(`/profile/${notification.actorId}`);
                                        }
                                    }
                                }}
                            />
                        )}
                        <span className="notification-title">
                            {getNotificationTitle(notification)}
                        </span>
                    </div>
                    <div className="notification-header-right">
                        {selectionMode && !notification._isInvite && (
                            <input
                                type="checkbox"
                                className="notification-select-checkbox"
                                checked={selectedIds.has(notification.id)}
                                onChange={() => toggleSelected(notification.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Select notification"
                            />
                        )}
                        <span className="timestamp">
                            {timeAgo(notification.createdAt)}
                        </span>
                        {!notification._isInvite && (
                            <Button
                                variant="ghost"
                                size="sm"
                                aria-label="More actions"
                                onClick={(e) => { e.stopPropagation(); toggleMenu(notification.id); }}
                                className="rounded-full !p-1 h-8 w-8 text-gray-400 hover:text-gray-600"
                            >
                                <IconDotsVertical size={18} />
                            </Button>
                        )}
                        {openMenuId === notification.id && (
                            <div className="notification-actions-menu" onClick={(e) => e.stopPropagation()}>
                                <button onClick={(e) => handleMarkRead(notification, e)} disabled={notification.read}>Mark as read</button>
                                <button onClick={(e) => handleDeleteNotification(notification, e)}>Delete</button>
                            </div>
                        )}
                    </div>
                </div>

                {notification.message && notification.message !== getNotificationTitle(notification) && (
                    <p className="notification-message">
                        {notification.message}
                    </p>
                )}

                {getNotificationPreview(notification) && (
                    <p className="notification-preview">
                        {getNotificationPreview(notification)}
                    </p>
                )}

                {notification.isAggregated && (
                    <div className="aggregation-indicator">
                        <IconUsers size={12} />
                        {notification.actorIds?.length || 2} people
                    </div>
                )}

                {notification.type === 'follow_request' && !notification._isInvite && !notification.requestStatus && (
                    <div className="invite-actions flex gap-2 mt-3">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleFollowRequestAccept(notification); }}
                            disabled={processingIds.has(notification.id)}
                            loading={processingIds.has(notification.id)}
                            className="rounded-full px-4"
                        >
                            Accept
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleFollowRequestReject(notification); }}
                            disabled={processingIds.has(notification.id)}
                            className="rounded-full px-4"
                        >
                            Decline
                        </Button>
                    </div>
                )}

                {notification.type === 'follow_request' && notification.requestStatus && (
                    <div className={`request-status ${notification.requestStatus}`}>
                        {notification.requestStatus === 'accepted' ? 'Accepted' : 'Declined'}
                    </div>
                )}

                {(notification.type === 'follow' || notification.type === 'follow_request_accepted') &&
                    notification.actorId &&
                    !notification._isInvite && (
                        <FollowBackButton actorId={notification.actorId} />
                    )}

                {notification._isInvite && (
                    <div className="invite-actions flex gap-2 mt-3">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleAccept(notification.id, notification)}
                            className="rounded-full px-4"
                        >
                            Accept
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDecline(notification.id, notification)}
                            className="rounded-full px-4"
                        >
                            Decline
                        </Button>
                    </div>
                )}
            </div>

            {!notification.read && !notification._isInvite && (
                <div className="unread-indicator" />
            )}
        </div>
    );
};
